import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Input from 'src/components/CustomInput';
import { useBatch } from 'src/hooks/services/useBatch';
import useConvertedInputHandler from 'src/hooks/useConvertedInputHandler';
import SingleMHSModuleContainer from 'src/pages/mline/sections/mhs/modules/single-mhs-module-container';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { MATERIAL_UNITS } from 'src/utils/constants';
import { convertToUserUnits, formatThreeDecimalsNumber, performConversion } from 'src/utils/conversions';

import RemainingQuantityProgressBar from './remaining-quantity-progress-bar';

const GeneralTrustCycloneForm = ({
  module,
  slot,
  trustCyclone,
  handleConfirmLoadFromInput,
  inputModuleTransactions,
  progressBarProps,
  handleRefetchCyclone,
}) => {
  const moduleName = module.name;
  const moduleType = module.type;

  const mhsInputBatchUri = module?.current_batch;

  const { remainingQtyPercentage, remainingQty, isAllTransactionsLoading } = progressBarProps;

  const { data: moduleBatch, isLoading: isModuleBatchLoading } = useBatch(mhsInputBatchUri, 'mhs-input-batch');

  const baseUnits = moduleBatch?.units;
  const batchQuantity = moduleBatch?.quantity;

  const [errorMessage, setErrorMessage] = useState('');
  const [isWeightValid, setIsWeightValid] = useState(true);

  const tareWeightInBatchUnits = useMemo(() => {
    if (baseUnits) {
      // Convert the tare weight from grams (source unit) to the batch's unit.
      return performConversion(module.tare_weight, MATERIAL_UNITS.GRAM, null, baseUnits);
    }
    return { quantity: 0, units: baseUnits, isConverted: false };
  }, [module.tare_weight, baseUnits]);

  const { quantity: displayedTareWeightQuantity } = useMemo(() => {
    if (baseUnits) {
      return convertToUserUnits(tareWeightInBatchUnits.quantity, baseUnits);
    }
    return { quantity: 0, units: '' };
  }, [tareWeightInBatchUnits.quantity, baseUnits]);


  // Use the custom hook for weight input
  const {
    baseValue: weightBase,
    convertedValue: weightConverted,
    handleInputChange: handleWeightChange,
    convertedUnits: weightUnits,
  } = useConvertedInputHandler(0, baseUnits);

  const { closeActionPanel } = useActionPanelStore();

  const validateWeight = (newWeightBase) => {
    // For trustCyclone mode, any positive value is valid
    if (trustCyclone) {
      if (newWeightBase < 0 && newWeightBase !== '') {
        setErrorMessage("Total Weight can't be less than 0");
        setIsWeightValid(false);
      } else {
        setErrorMessage('');
        setIsWeightValid(true);
      }
      return;
    }

    // For non-trustCyclone mode: Total weight can't be less than tare weight
    if (newWeightBase < displayedTareWeightQuantity) {
      setErrorMessage("Total Weight can't be less than Tare Weight");
      setIsWeightValid(false);
    } else {
      setErrorMessage('');
      setIsWeightValid(true);
    }
  };

  const handleWeightInputChange = (e) => {
    handleWeightChange(e);
    validateWeight(parseFloat(e.target.value) || 0); // Validate in base units
  };

  // Calculate reclaimed weight based on mode
  let reclaimedWeightBase;
  if (trustCyclone) {
    // In trustCyclone mode, the entered weight IS the reclaimed weight
    reclaimedWeightBase = weightBase > 0 ? weightBase : 0;
  } else {
    // In normal mode, reclaimed weight is total weight minus tare
    reclaimedWeightBase = weightBase > 0 ? weightBase - tareWeightInBatchUnits.quantity : 0;
  }

  const consumedDoseWeightBase = trustCyclone
    ? 0 // We don't track consumed in trustCyclone mode for this form
    : (batchQuantity - reclaimedWeightBase);

  const reclaimedWeightBaseFormatted = formatThreeDecimalsNumber(reclaimedWeightBase);
  const requestedQuantityMoreThanAvailable = remainingQty < reclaimedWeightBase;

  // Calculate percentage of reclaimed weight relative to initial quantity
  const totalReclaimedWeightPercentage = progressBarProps.initialQty > 0
    ? Math.round((reclaimedWeightBase / progressBarProps.initialQty) * 100)
    : 0;

  // Determine if action details should be shown
  const shouldShowActionDetails = weightBase > 0;

  const shouldShowWeightDetails = isWeightValid && weightBase > 0;

  // Weight loss detection
  const { isLastTransaction, weightLossThreshold, initialQty } = progressBarProps;
  const hasEnteredValue = weightBase > 0;
  const isWeightLoss = isLastTransaction && hasEnteredValue && reclaimedWeightBase < remainingQty;
  const weightLossAmount = isWeightLoss ? remainingQty - reclaimedWeightBase : 0;
  const weightLossPercentage = isWeightLoss && initialQty > 0
    ? Math.round((weightLossAmount / initialQty) * 100)
    : 0;
  const isSignificantWeightLoss = isWeightLoss && weightLossPercentage > weightLossThreshold;

  const handleSubmit = async () => {
    if (isWeightValid && weightBase >= 0) {
      await handleConfirmLoadFromInput(moduleType, mhsInputBatchUri, reclaimedWeightBase, consumedDoseWeightBase)
    }
  };

  return (
    <div>
      <SingleMHSModuleContainer
        skipActions
        customStatusText={moduleName}
        inputModuleTransactions={inputModuleTransactions}
        module={module}
        slot={slot}
        handleRefetchCyclone={handleRefetchCyclone}
      >
        <div className="d-flex align-items-center justify-content-between">
          {/* TODO: GE Temporarily removed */}
          {/*<div>Expected Weight: {expectedWeight} lb</div>*/}
          {isModuleBatchLoading ? <Spinner animation="border" /> : (
            <div>Tare:&nbsp;
              <ConvertedUnits quantity={tareWeightInBatchUnits.quantity} units={tareWeightInBatchUnits.units} />
            </div>
          )}
        </div>
      </SingleMHSModuleContainer>

      <Input
        label={trustCyclone
          ? "Weight of the Reclaimed Powder:"
          : "Total Weight before Reclaim:"}
        type="number"
        value={weightConverted}
        suffix={weightUnits}
        isError={!isWeightValid}
        style={{ marginBottom: '15px' }}
        errorMessage={errorMessage}
        onChange={handleWeightInputChange}
      />

      {shouldShowWeightDetails && (
        <div className="mb15">
          {!trustCyclone && <>Reclaimed Weight:&nbsp;</>}
          <strong>
            <ConvertedUnits quantity={reclaimedWeightBaseFormatted} units={baseUnits} />
            <span
              className={`ml-2 badge ${
                requestedQuantityMoreThanAvailable ? 'badge-danger' : 'badge-success'
              }`}
            >
              {totalReclaimedWeightPercentage}%
            </span>
          </strong>
        </div>
      )}

      <RemainingQuantityProgressBar
        remainingQty={remainingQty}
        remainingQtyPercentage={remainingQtyPercentage}
        baseUnits={baseUnits}
        actionWeight={reclaimedWeightBase}
        isActionWeightLoading={isAllTransactionsLoading}
        showActionDetails={shouldShowActionDetails}
        initialQty={initialQty}
        reconciledQtyPercentage={progressBarProps.reconciledQtyPercentage}
      />

      {shouldShowWeightDetails && requestedQuantityMoreThanAvailable && (
        <Alert
          name="qr-instructions"
          variant="danger"
          className="text-left d-flex align-items-center mt15"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="font-size-22 mr15" />
          <div>
            <div>
              The quantities you have entered have exceeded the amount of
              material available to be reconciled by&nbsp;
              <ConvertedUnits
                className="font-weight-bold"
                quantity={formatThreeDecimalsNumber(reclaimedWeightBase - remainingQty)}
                units={baseUnits}
              />.
              Please double check your data and if an error has been made when
              reconciling a previous module, please speak to your manager.
            </div>
          </div>
        </Alert>
      )}

      {isWeightValid && hasEnteredValue && isSignificantWeightLoss && (
        <Alert
          name="qr-weight-loss"
          variant="warning"
          className="text-left d-flex align-items-center mt15"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="font-size-22 mr15" />
          <div>
            <div>
              There is&nbsp;
              <ConvertedUnits
                className="font-weight-bold"
                quantity={formatThreeDecimalsNumber(weightLossAmount)}
                units={baseUnits}
              />&nbsp;
              <strong>({weightLossPercentage}%)</strong> of unaccounted material after completing the last transaction.
              This exceeds the <strong>{weightLossThreshold}%</strong> threshold. Please double check your data and if this material loss is correct, please speak to your manager before completing this transaction.
            </div>
          </div>
        </Alert>
      )}

      <button
        type='button'
        disabled={!isWeightValid || weightBase < 0 || requestedQuantityMoreThanAvailable}
        className='btn btn-lg btn-primary action-btn btn-block mt60'
        onClick={handleSubmit}
      >
        Update
      </button>
      <button type='button' className='btn btn-lg btn-block' onClick={closeActionPanel}>
        Cancel
      </button>
    </div>
  );
};

GeneralTrustCycloneForm.propTypes = {
  /* eslint-disable camelcase */
  module: PropTypes.shape({
    name: PropTypes.string,
    tare_weight: PropTypes.number,
    type: PropTypes.string,
    current_batch: PropTypes.string,
  }).isRequired,
  slot: PropTypes.string.isRequired,
  trustCyclone: PropTypes.bool.isRequired,
  handleConfirmLoadFromInput: PropTypes.func.isRequired,
  inputModuleTransactions: PropTypes.arrayOf(PropTypes.shape({
    parent_transaction: PropTypes.string,
    initial_quantity_available: PropTypes.number,
  })).isRequired,
  progressBarProps: PropTypes.shape({
    remainingQtyPercentage: PropTypes.number,
    remainingQty: PropTypes.number,
    isAllTransactionsLoading: PropTypes.bool,
    initialQty: PropTypes.number,
    reconciledQtyPercentage: PropTypes.number,
    isLastTransaction: PropTypes.bool,
    weightLossThreshold: PropTypes.number,
  }).isRequired,
  handleRefetchCyclone: PropTypes.func.isRequired,
};

export default GeneralTrustCycloneForm;
